import { useState, MouseEvent, ReactElement, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { observer } from "mobx-react";
import cls from "classnames";
import useStateRef from "logic/src/utils/useStateRef";
import { Input, Popover, Tooltip } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import i18next from "logic/src/i18n";
import useMessage from "logic/src/utils/useMessage";
import Chat from "logic/src/objects/chat/model";
import { REQUEST_ERR_TOAST } from "logic/src/constants/request";
import { ReactComponent as ArrowRight } from "logic/src/components/icons/arrowRight.svg";
import {
  EChatErrorCode,
  SensitiveTips,
} from "logic/src/objects/chat/constants";
import ErrorBoundary from "logic/src/components/ErrorBoundary";
import {
  EChatScore,
  EChatStatus,
  EChatType,
  ERegenerateJudge,
  EStreamDataStatus,
  EChatModel,
  EStreamDataType,
} from "logic/src/objects/chat/types";
import SJToast from "@/components/SJToast";
import SJButton from "@/components/SJButton";
import type {
  IInfo,
  IThinkingInfo,
  IWebSearchInfo,
} from "logic/src/objects/chat/BaseStreamRenderV2";
import StreamRenderV2, {
  IRenderContentEvent,
} from "@/pages/Chat_v2/utils/StreamRenderV2";
// import DotPulse from "@/components/DotPulse";
import WaitingText from "@/components/WaitingText";
import Stop from "../Stop";
import AiContentTip from "logic/src/objects/session/components/AiContentTip";
import { ReactComponent as CloseIcon } from "logic/src/components/icons/cross.svg";
import { ReactComponent as DisLikeIcon } from "logic/src/components/icons/dislike.svg";
import { ReactComponent as DisLikeFillIcon } from "logic/src/components/icons/dislikeFill.svg";
import { ReactComponent as LikeIcon } from "logic/src/components/icons/like.svg";
import { ReactComponent as LikeFillIcon } from "logic/src/components/icons/likeFill.svg";
import { ReactComponent as CopyIcon } from "logic/src/components/icons/copy.svg";
import { ReactComponent as CycleIcon } from "logic/src/components/icons/cycle.svg";
import { ReactComponent as AvatarIcon } from "logic/src/components/icons/logo.svg";
import Thinking from "../Thinking";

import styles from "./index.module.scss";
import contentStyles from "./content.module.scss";
import { IVideoFileInfo } from "logic/src/objects/video/types";
import { IWebSearch } from "logic/src/objects/chat/types";
import { sendCLK } from "logic/src/utils/eventTrack";

const TAG_LIST = [
  i18next.t("sensechat-310"), // "包含有害或违规信息",
  i18next.t("sensechat-311"), // "有错误内容",
  i18next.t("sensechat-312"), // "答案与问题无关",
  i18next.t("sensechat-313"), // "没有什么帮助"
];

interface IProps {
  videoInfoList?: IVideoFileInfo[];
  isLast: boolean;
  chatInfo: Chat;
  onWebSearchClick?: (v: IWebSearch[]) => void;
  onInputFocus?: () => void;
  onChatErrorRefresh?: () => void;
  onReAsk: (v: Chat) => void;
  onVideoTimeClick?: (v: { seekTime: string; videoIndex: number }) => void;
  onStreamEnd?: (sessionId: string, chatInfo: Chat) => void;
}

const PopAI: React.FC<IProps> = ({
  isLast,
  chatInfo,
  onInputFocus,
  onChatErrorRefresh,
  onReAsk,
  videoInfoList,
  onVideoTimeClick,
  onWebSearchClick,
}) => {
  const { t } = useTranslation();
  const messageApi = useMessage();
  const [fold, setFold] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [inputTag, setInputTag] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const [richContent, setRichContent] = useState<ReactElement[]>([]);
  const [thinkingInfo, setThinkingInfo] = useStateRef<IThinkingInfo | null>(
    null,
  );
  const [webSearchInfo, setWebSearchInfo, webSearchInfoRef] =
    useStateRef<IWebSearchInfo | null>(null);

  const [streamRender] = useState<StreamRenderV2>(() => {
    const render = new StreamRenderV2({
      baseRender: chatInfo.render,
      onContentChange: (
        list: ReactElement[],
        originList: IInfo[],
        thinkingInfo: IThinkingInfo | null,
      ) => {
        setRichContent(list);
        setThinkingInfo(thinkingInfo);
        const webSearch = originList.find(
          ele => ele.type === EStreamDataType.web_searcher,
        );
        if (webSearch && !webSearchInfoRef.current) {
          setWebSearchInfo(webSearch as IWebSearchInfo);
        }
      },
    });
    if (chatInfo.chatModel !== EChatModel.Normal) {
      render.setPureMarkdown(true);
    }
    if (videoInfoList) {
      render.setVideoInfoList(videoInfoList);
      render.setSessionId(chatInfo.session_id || "");
    }
    render.setOnEvent((e: IRenderContentEvent) => {
      if (e.type === "videoTimeClick") {
        onVideoTimeClick?.(e.data);
      } else if (e.type === "webSearchClick") {
        onWebSearchClick?.(e.data);
      }
    });

    return render;
  });

  // 展示stop
  const stopVisible =
    !chatInfo.guidance &&
    (chatInfo.streamingStatus === EStreamDataStatus.streaming ||
      chatInfo.streamingStatus === EStreamDataStatus.init);

  // 展示重新生成
  const showReAsk =
    chatInfo.message_id &&
    // !chatInfo.guidance &&
    chatInfo.streamingStatus === EStreamDataStatus.streamingEnd &&
    isLast;

  // 是否展示赞踩操作栏
  const showAction =
    !chatInfo.guidance &&
    !!chatInfo.message_id &&
    !chatInfo.errCode &&
    (chatInfo.type === EChatType.history ||
      chatInfo.streamingStatus === EStreamDataStatus.streamingEnd);

  // 展示复制
  const showCopyIcon =
    chatInfo.type === EChatType.history ||
    chatInfo.streamingStatus === EStreamDataStatus.streamingEnd;

  // 点踩提交
  const handleSubmitDislike = async () => {
    const allSelectedTags = [...selectedTags];
    if (
      chatInfo.chatModel === EChatModel.Normal ||
      chatInfo.chatModel === EChatModel.R1
    ) {
      sendCLK(
        "text_chat_feedback_dislike",
        "文本聊天_点踩按钮点击_" + allSelectedTags.join("_"),
      );
    } else {
      sendCLK(
        "video_feedback_dislike",
        "视频理解_点踩按钮点击_" + allSelectedTags.join("_"),
      );
    }
    !!inputTag.trim() && allSelectedTags.push(inputTag);
    if (!allSelectedTags.length) return;

    setOpen(false);
    setSelectedTags(allSelectedTags);
    chatInfo.feedback({
      score: EChatScore.dislike,
      tag: allSelectedTags,
      onSuccess: () => {
        setInputTag("");
        messageApi.success({
          content: t("sensechat-38"), // "感谢您的反馈意见",
          className: "sj-success-message",
        });
      },
      onFailure: () => {
        messageApi.info({
          content: REQUEST_ERR_TOAST,
          className: "sj-info-message",
        });
      },
    });
  };

  // 显示点踩弹框
  const handleDislikeFeedback = () => {
    setSelectedTags([]);
    setOpen(true);
  };

  const handleSelectTag = (tag: string) => {
    const tagList = selectedTags.includes(tag)
      ? selectedTags.filter(ele => ele !== tag)
      : selectedTags.concat([tag]);
    setSelectedTags(tagList);
  };

  const handleInputTagChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputTag(e.target.value);
  };

  const handleFeedback = (score: EChatScore) => {
    if (score === EChatScore.like) {
      if (
        chatInfo.chatModel === EChatModel.Normal ||
        chatInfo.chatModel === EChatModel.R1
      ) {
        sendCLK("text_chat_feedback_like", "文本聊天_点赞按钮点击");
      } else {
        sendCLK("video_feedback_like", "视频理解_点赞按钮点击");
      }
    }

    chatInfo.feedback({
      score,
      onFailure: () => SJToast.info(REQUEST_ERR_TOAST),
    });
  };

  const handleStop = () => {
    sendCLK("text_chat_interrupt_reply", "文本聊天_停止生成按钮点击");
    chatInfo.stop();
    onInputFocus?.();
  };

  const handleReAsk = async () => {
    // await chatInfo.reAsk();
    // onInputFocus?.();
    sendCLK("text_chat_regenerate", "文本聊天_重新生成按钮点击");
    onReAsk(chatInfo);
  };

  const handleRegenJudge = async (v: ERegenerateJudge) => {
    await chatInfo.judgeRegenerateMsg(v);
  };

  const handleRefresh = () => {
    onChatErrorRefresh ? onChatErrorRefresh() : location.reload();
  };

  // 点踩 popover
  const dislikePopoverContent = (
    <div className={styles.feedback}>
      <div className={styles.wordfont}>
        {TAG_LIST.map((tag: string) => (
          <div
            key={tag}
            onClick={() => handleSelectTag(tag)}
            className={cls(
              styles["word-item"],
              selectedTags.includes(tag) && styles["word-item-selected"],
            )}
          >
            {tag}
          </div>
        ))}
      </div>
      <Input.TextArea
        maxLength={2000}
        value={inputTag}
        onChange={e => handleInputTagChange(e)}
        placeholder={t("sensechat-245") as string} // 补充我们尚未发现的原因
        style={{
          marginTop: "8px",
          resize: "none",
          border: "none",
        }}
      />
      <div className={styles["modal-footer-box"]}>
        <SJButton
          type="primary"
          className={styles.feedbackSubmitBtn}
          disabled={!selectedTags.length && !inputTag.length}
          onClick={handleSubmitDislike}
        >
          {/* 提交 */}
          {t("sensechat-22")}
        </SJButton>
      </div>
    </div>
  );

  // 点赞icon
  const likeAction =
    chatInfo.score === EChatScore.like ? (
      <Tooltip
        placement="bottom"
        trigger="hover"
        color="#242424"
        title="取消喜欢"
      >
        <LikeFillIcon
          className={styles.actionFillIcon}
          onClick={() => handleFeedback(EChatScore.default)}
        />
      </Tooltip>
    ) : (
      <Tooltip placement="bottom" trigger="hover" color="#242424" title="喜欢">
        <LikeIcon
          className={styles.actionIcon}
          onClick={() => handleFeedback(EChatScore.like)}
        />
      </Tooltip>
    );

  // 点踩icon
  const dislikeAction =
    chatInfo.score === EChatScore.dislike ? (
      <Tooltip
        placement="bottom"
        trigger="hover"
        color="#242424"
        title="取消不喜欢"
      >
        <DisLikeFillIcon
          className={styles.actionFillIcon}
          onClick={() => handleFeedback(EChatScore.default)}
        />
      </Tooltip>
    ) : (
      <Popover
        open={open}
        arrow={false}
        trigger="click"
        onOpenChange={val => setOpen(val)}
        title={
          <div className={styles.feedbackTitle}>
            {/* 我们需要您的反馈 */}
            <span>{t("sensechat-241")}</span>
            <CloseIcon
              onClick={() => setOpen(false)}
              style={{ fontSize: "20px" }}
            />
          </div>
        }
        content={dislikePopoverContent}
      >
        <Tooltip
          placement="bottom"
          trigger="hover"
          color="#242424"
          title="不喜欢"
        >
          <DisLikeIcon
            onClick={handleDislikeFeedback}
            className={styles.actionIcon}
          />
        </Tooltip>
      </Popover>
    );

  // useEffect(() => {
  //   if (sseData) {
  //     if (sseData.reasoning_content === undefined) {
  //       streamRender.write(sseData);
  //       if (thinkingInfoRef.current && !thinkingInfoRef.current?.endTime) {
  //         setThinkingInfo({
  //           ...thinkingInfoRef.current,
  //           endTime: new Date().getTime(),
  //         });
  //       }
  //     } else {
  //       if (thinkingInfoRef.current) {
  //         setThinkingInfo({
  //           ...thinkingInfoRef.current,
  //           text: thinkingInfoRef.current.text + sseData.reasoning_content,
  //         });
  //       } else {
  //         setThinkingInfo({
  //           type: EStreamDataType.thinking,
  //           text: sseData.reasoning_content?.trimLeft?.(),
  //           startTime: new Date().getTime(),
  //           id: EStreamDataType.thinking + "" + new Date().getTime(),
  //         });
  //       }
  //     }

  //     if (sseData.status === EChatRecvStatus.end) {
  //       onStreamEnd?.(chatInfo.session_id || "", chatInfo);
  //     }
  //   }
  // }, [sseData]);

  // useEffect(() => {
  //   if (msgList && !sseData) {
  //     streamRender.clear();
  //     streamRender.batchWrite(msgList);
  //     const lastMsg = last(msgList);
  //     if (lastMsg && lastMsg.status !== EChatRecvStatus.end) {
  //       streamRender.write({
  //         type: EStreamDataType.status,
  //         status: EChatRecvStatus.end,
  //       });
  //     }
  //     const firstMsg = msgList[0];
  //     if (firstMsg?.reasoning_content) {
  //       setThinkingInfo({
  //         type: EStreamDataType.thinking,
  //         text: firstMsg.reasoning_content?.trimLeft?.(),
  //         startTime: new Date().getTime(),
  //         endTime: new Date().getTime(),
  //         id: EStreamDataType.thinking + "" + new Date().getTime(),
  //       });
  //     }
  //   }
  // }, [msgList, sseData]);

  useEffect(() => {
    streamRender.setVideoInfoList(videoInfoList || null);
  }, [videoInfoList]);

  useEffect(() => {
    streamRender.setSessionId(chatInfo.session_id || "");
  }, [chatInfo.session_id]);

  const handleCopy = (event: MouseEvent) => {
    sendCLK("text_chat_copy", "文本聊天_复制按钮点击");
    chatInfo.handleCopy(
      event,
      () => {
        messageApi.success({
          content: t("sensechat-62"), // 复制成功，内容由AI生成
          className: "sj-success-message",
        });
      },
      thinkingInfo ? fold : false,
    );
  };

  const showErrorTip =
    chatInfo.status !== EChatStatus.key_point &&
    chatInfo.errCode &&
    chatInfo.errCode !== EChatErrorCode.thinkSensitive &&
    !chatInfo.isSensitive &&
    !chatInfo.isBusy;

  console.log("handleOnMessage--3--", richContent);
  return (
    <>
      <div
        className={cls(
          styles.container,
          isLast && styles.last,
          isLast && chatInfo.canRegenFeedback && styles.height,
        )}
      >
        <ErrorBoundary>
          {/* <AiContentTip /> */}
          <AvatarIcon className={styles["avatar-icon"]} />

          <div className={styles.body}>
            {webSearchInfo && (
              <div
                className={styles.websearch}
                key={webSearchInfo?.id}
                onClick={() => onWebSearchClick?.(webSearchInfo.weabSearch)}
              >
                <span>
                  {`找到了 ${(webSearchInfo as IWebSearchInfo).weabSearch?.length} 篇资料作为参考  `}
                </span>
                <ArrowRight className={styles.websearchIcon} />
              </div>
            )}

            {thinkingInfo && (
              <Thinking
                sensitive={chatInfo.isThinkSensitive}
                fold={fold}
                toggleFold={() => setFold(v => !v)}
                info={thinkingInfo}
                stoped={chatInfo.complete === false}
              />
            )}
            {(!thinkingInfo || thinkingInfo.endTime) && (
              <div className={styles.mainContent}>
                <AiContentTip />
                <div className={styles.content}>
                  {chatInfo.streamingStatus === EStreamDataStatus.init ? (
                    <WaitingText chatModel={chatInfo.chatModel} />
                  ) : (
                    <div
                      className={cls(
                        contentStyles["msg-container"],
                        chatInfo.streamingStatus ===
                          EStreamDataStatus.streaming &&
                          contentStyles.streamingContent,
                      )}
                    >
                      <div className={contentStyles.text}>{richContent}</div>
                    </div>
                    // <PopAIContent
                    //   type={chatInfo.type}
                    //   msgList={
                    //     chatInfo.type === EChatType.history
                    //       ? chatInfo.recv_msg || undefined
                    //       : undefined
                    //   }
                    //   sseData={
                    //     chatInfo.streamingStatus === EStreamDataStatus.streaming
                    //       ? chatInfo.current_msg
                    //       : undefined
                    //   }
                    // />
                  )}

                  {/* 用于划重点 */}
                  {chatInfo.status === EChatStatus.key_point && (
                    <div>好的，已划线。</div>
                  )}

                  {/* 敏感提示*/}
                  {chatInfo.isSensitive && (
                    <div className={styles.sensitiveTip}>
                      {chatInfo.isContentEmpty
                        ? SensitiveTips.noAIContent
                        : SensitiveTips.hasAIContent}
                    </div>
                  )}
                  {/* 用户忙提示 */}
                  {chatInfo.isBusy && <div>正在回答上一个问题，请稍后再试</div>}

                  {/* 错误提示 */}
                  {showErrorTip && (
                    <div className={styles["error-wrap"]}>
                      <ExclamationCircleFilled
                        className={styles["error-icon"]}
                      />
                      {chatInfo.errCode === EChatErrorCode.modelError ? (
                        <span>{t("sensechat-190")}</span> // 服务异常，未能完成回复。
                      ) : (
                        <Trans>
                          {[180006, 180007, 180008, 180009].includes(
                            chatInfo.errCode!,
                          )
                            ? "出了点错误，请稍后再试，"
                            : "网络繁忙，请稍后再试，"}
                          <a
                            className={styles.reloadLink}
                            onClick={handleRefresh}
                          >
                            点击重试
                          </a>
                        </Trans>
                      )}
                    </div>
                  )}

                  {/* 回答停止 */}
                  {chatInfo.complete === false && (
                    //（本回答已被停止）
                    <div className={styles["block-tip"]}>
                      {t("sensechat-274")}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* 点赞点踩复制 */}
          {(showAction || showCopyIcon || showReAsk) && (
            <div className={styles.interact}>
              {showAction && (
                <>
                  {likeAction}
                  {dislikeAction}
                </>
              )}
              {showCopyIcon && (
                <Tooltip
                  placement="bottom"
                  trigger="hover"
                  color="#242424"
                  title="复制"
                >
                  <CopyIcon
                    className={styles.actionIcon}
                    onClick={(event: MouseEvent) => handleCopy(event)}
                  />
                </Tooltip>
              )}
              {showReAsk && (
                <Tooltip
                  placement="bottom"
                  trigger="hover"
                  color="#242424"
                  title="重新回答"
                >
                  <CycleIcon
                    className={styles.actionIcon}
                    onClick={handleReAsk}
                  />
                </Tooltip>
              )}
            </div>
          )}
          <Stop
            onStop={handleStop}
            disabled={chatInfo.streamingStatus === EStreamDataStatus.init}
            visible={stopVisible}
          />

          {/* 重新生成评价  */}
          {isLast && chatInfo.canRegenFeedback && (
            <div className={styles.compareWrap}>
              <p>相较上条回答，此回复是更好还是更糟？</p>
              <div className={styles.btnWrap}>
                <span onClick={() => handleRegenJudge(ERegenerateJudge.better)}>
                  更好
                </span>
                <span onClick={() => handleRegenJudge(ERegenerateJudge.worse)}>
                  更差
                </span>
                <span onClick={() => handleRegenJudge(ERegenerateJudge.same)}>
                  一般
                </span>
                <CloseIcon
                  onClick={() => handleRegenJudge(ERegenerateJudge.cancel)}
                />
              </div>
            </div>
          )}

          {messageApi.contextHolder}
        </ErrorBoundary>
      </div>
    </>
  );
};

export default observer(PopAI);
